import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  emailSettingsResultSchema,
  signatureResultSchema,
  smtpSaveResultSchema,
  smtpTestResultSchema,
  type EmailSettingsResult,
  type SaveSmtpInput,
  type SignatureResult,
  type SmtpSaveResult,
  type SmtpTestResult,
} from '../schemas';
import { useToken } from './use-token';

/**
 * L'adresse d'expédition du cabinet, sa signature, le lien de sa fiche Google.
 *
 * Lecture ouverte à tous les membres — la réponse ne contient aucun secret, le
 * mot de passe SMTP n'étant jamais renvoyé. Les écritures ci-dessous sont,
 * elles, réservées aux admins du cabinet : l'API répond `403` sinon.
 */
export function useEmailSettings() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.emailSettings.detail(),
    queryFn: () =>
      api.get<EmailSettingsResult>('/email-settings', { schema: emailSettingsResultSchema }),
    enabled: token !== null,
  });
}

/**
 * Déclare ou corrige le compte d'envoi.
 *
 * Le compte est enregistré **puis** testé : `data.testError` porte le refus du
 * serveur quand il y en a un, et la saisie n'est jamais perdue. Mot de passe
 * vide en retouche = inchangé.
 */
export function useSaveSmtpAccount() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveSmtpInput) =>
      api.put<SmtpSaveResult>('/email-settings/smtp', input, { schema: smtpSaveResultSchema }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() });
      // L'écran des messages affiche si l'envoi est configuré.
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() });
    },
  });
}

/**
 * Révoque le compte d'envoi : la ligne est supprimée côté serveur, mot de
 * passe compris.
 *
 * Les messages du cabinet repartent alors par le relais de la plateforme tant
 * qu'il lui reste du forfait, et s'arrêtent au-delà — d'où la confirmation
 * côté écran.
 */
export function useDeleteSmtpAccount() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.delete('/email-settings/smtp'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() });
      // L'écran des messages affiche si l'envoi est configuré, et le forfait
      // de relais qu'il reste.
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() });
    },
  });
}

/**
 * Teste la connexion au serveur du cabinet, sans rien envoyer.
 *
 * Un refus arrive en `502 mail_delivery_failed`, message du serveur distant
 * repris tel quel : c'est la seule information utile au cabinet.
 */
export function useTestSmtpAccount() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<SmtpTestResult>('/email-settings/smtp/test', undefined, {
        schema: smtpTestResultSchema,
      }),
    // `onSettled` : le résultat du test est enregistré par le serveur qu'il
    // réussisse ou non, et l'écran doit montrer les deux.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() }),
  });
}

/** Signature du cabinet, apposée au bas de tous ses messages. */
export function useSaveSignature() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { body: string; withLogo: boolean }) =>
      api.put<SignatureResult>('/email-settings/signature', input, {
        schema: signatureResultSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() }),
  });
}
